import { assign } from "lodash";
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import * as FlatModels from "@medsurf/flat-models";
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from "@medsurf/flat-validators";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'medsurf-slide-edit-free-form-path-form',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './free-form-path-form.component.html',
    styleUrls: ['./free-form-path-form.component.scss'],
    standalone: false
})
export class FreeFormPathFormComponent {
  #isSubmitted = signal(false);
  #freeForm: FlatModels.AnnotationEntityModels.FreeForm;
  #freeFormStyle: FlatModels.FreeFormStyleEntityModels.FreeFormStyle;
  freeFormStyleForm = new FormGroup(
    {
      closePath: new FormControl(false),
      smooth: new FormControl(false),
      smoothFactor: new FormControl(0),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.FreeFormStyleEntityModels.FreeFormStyleSchema.pick({
          closePath: true,
          smooth: true,
          smoothFactor: true,
        })
      )
    }
  );
  readonly FlatModels = FlatModels;
  
  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade) {
    // Update Free Form
    this.slideAuthorFacade.currentSelectedSlideAnnotation$.pipe(takeUntilDestroyed()).subscribe((freeForm) => {
      if (freeForm) {
        this.#freeForm = freeForm as FlatModels.AnnotationEntityModels.FreeForm;
      }
    });
    
    // Update Free Form Style
    this.slideAuthorFacade.currentSelectedSlideFreeFormStyle$.pipe(takeUntilDestroyed()).subscribe((freeFormStyle) => {
      this.#freeFormStyle = structuredClone(freeFormStyle);
      if (freeFormStyle) {
        this.freeFormStyleForm.reset({
          closePath: this.#freeFormStyle.closePath,
          smooth: this.#freeFormStyle.smooth,
          smoothFactor: this.#freeFormStyle.smoothFactor * 100,
        });
      }
      this.#isSubmitted.set(false);
    });
  }
  
  onSubmit() {
    if (this.#isSubmitted() || this.freeFormStyleForm.invalid) {
      return;
    }
    this.#isSubmitted.set(true);
    
    const freeFormStyle = assign(
      this.#freeFormStyle,
      {
        closePath: this.freeFormStyleForm.value.closePath,
        smooth: this.freeFormStyleForm.value.smooth,
        smoothFactor: (this.freeFormStyleForm.value.smoothFactor || 0) / 100,
      }
    );
    this.slideAuthorFacade.requestUpdateFreeFormStyle(freeFormStyle, 'history.free_form_update_style').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.freeFormStyleForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
  
  drawFreeForm() {
    this.slideAuthorFacade.requestActivateDrawTool(this.#freeForm).subscribe({
      error: (result) => {
        this.alertControlFacade.requestAddError(result);
        this.#isSubmitted.set(false);
      },
    });
  }
}
